import React, { useState } from 'react';
import Navbar from '../../components/navbar/Navbar';
import Footer from '../../components/footer/Footer';
import Container from '../../components/Container';
import { ChevronDown, ChevronRight, AlertTriangle, Calendar, Stethoscope, Phone, Mail, Shield, Clock, Users } from 'lucide-react';

const AccordionItem = ({ title, children, isOpen, onToggle }) => (
  <div className="border border-gray-200 rounded-lg mb-4">
    <button
      onClick={onToggle}
      className={`w-full px-4 sm:px-6 py-3 sm:py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors ${isOpen ? 'bg-gray-50' : ''}`}
    >
      <h3 className="text-base sm:text-lg font-semibold text-gray-900 pr-2">{title}</h3>
      {isOpen ? (
        <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5 text-[#ab3d22] flex-shrink-0" />
      ) : (
        <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 flex-shrink-0" />
      )}
    </button>
    {isOpen && (
      <div className="px-4 sm:px-6 pt-3 sm:pt-4 pb-3 sm:pb-4">
        <div className="text-gray-700 leading-relaxed text-sm sm:text-base">
          {children}
        </div>
      </div>
    )}
  </div>
);

const CalloutBox = ({ children, type = "info" }) => {
  const bgColor = type === "important" ? "bg-[#ab3d22]/5" : "bg-[#fbf6f3]";
  const borderColor = type === "important" ? "border-[#ab3d22]/20" : "border-gray-200";
  
  return (
    <div className={`${bgColor} ${borderColor} border rounded-lg p-3 sm:p-4 my-4 sm:my-6`}>
      {children}
    </div>
  );
};

const MedicalDisclaimerPage = () => {
  const [openSections, setOpenSections] = useState({
    notMedicalAdvice: true,
    consultProfessionals: false,
    patchTesting: false,
    emergencySituations: false
  });

  const toggleSection = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <div className="min-h-screen bg-[#f8f8f8]">
      <Navbar />
      
      {/* Editorial Hero Banner */}
      <section className="relative overflow-hidden">
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-gray-50 to-zinc-50"></div>
        
        {/* Subtle Glow Pattern */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-radial from-slate-200/20 to-transparent rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/3 right-1/3 w-80 h-80 bg-gradient-radial from-zinc-200/20 to-transparent rounded-full blur-3xl"></div>
        </div>
        
        <Container className="relative py-8 sm:py-12 md:py-16">
          <div className="text-center max-w-2xl mx-auto px-4">
            {/* Editorial Accent */}
            <div className="mb-4 sm:mb-6">
              <span className="text-xs sm:text-sm tracking-[0.2em] text-gray-500 uppercase font-light italic" style={{fontFamily: 'serif'}}>
                LEGAL
              </span>
            </div>
            
            {/* Main Headline */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 sm:mb-6 relative" style={{fontFamily: 'Poppins, sans-serif', fontWeight: 700}}>
              Medical Disclaimer
              {/* Brand Accent Line */}
              <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-16 sm:w-24 h-1 bg-[#ab3d22] rounded-full"></div>
            </h1>
            
            {/* Refined Tagline */}
            <p className="text-base sm:text-lg md:text-xl text-gray-500 leading-relaxed mb-6 sm:mb-8 md:mb-10 max-w-3xl mx-auto font-light">
              Important information about the limitations of our content and recommendations
            </p>
            
            {/* CTA Button */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center">
              <a 
                href="/contact" 
                className="inline-flex items-center justify-center bg-[#ab3d22] hover:bg-[#8a2f1a] text-white font-medium px-6 sm:px-8 py-3 sm:py-4 rounded-xl text-sm transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 w-full sm:w-auto"
              >
                Contact Our Team
                <Mail className="ml-2 h-4 w-4" />
              </a>
              <a 
                href="/editorial-standards" 
                className="inline-flex items-center justify-center bg-white hover:bg-gray-50 text-gray-700 font-medium px-6 sm:px-8 py-3 sm:py-4 rounded-xl text-sm transition-all duration-200 shadow-md hover:shadow-lg border border-gray-200 w-full sm:w-auto"
              >
                Editorial Standards
                <Shield className="ml-2 h-4 w-4" />
              </a>
            </div>
          </div>
        </Container>
      </section>

      <Container className="py-8 sm:py-10">
        <div className="max-w-4xl mx-auto">
          {/* Last Updated */}
          <div className="flex items-center gap-2 mb-8 text-sm text-gray-500">
            <Calendar className="h-4 w-4" />
            <span>Last Updated: September 12, 2025</span>
          </div>

          {/* Not Medical Advice */}
          <div id="not-medical-advice">
            <AccordionItem
              title="Not Medical Advice"
              isOpen={openSections.notMedicalAdvice}
              onToggle={() => toggleSection('notMedicalAdvice')}
            >
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Important Disclaimer</h4>
                    <p className="text-sm text-gray-600">
                      The information provided on the Radiance website is for informational and educational 
                      purposes only and is not intended as medical advice. Our content should not be used 
                      to diagnose, treat, cure, or prevent any medical condition or disease.
                    </p>
                  </div>
                </div>
                
                <CalloutBox type="important">
                  <div className="flex items-start gap-3">
                    <Stethoscope className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-900 mb-1">Professional Medical Advice Required</p>
                      <p className="text-sm">
                        Always consult qualified healthcare professionals for medical concerns. 
                        Our recommendations are not a substitute for professional medical advice.
                      </p>
                    </div>
                  </div>
                </CalloutBox>
              </div>
            </AccordionItem>
          </div>

          {/* Consult Healthcare Professionals */}
          <AccordionItem
            title="Consult Healthcare Professionals"
            isOpen={openSections.consultProfessionals}
            onToggle={() => toggleSection('consultProfessionals')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600 mb-4">
                Always consult with qualified healthcare professionals, including dermatologists, 
                physicians, or other licensed medical practitioners, before:
              </p>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">When to Consult a Professional:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Starting any new skincare routine or treatment</li>
                  <li>• Using products containing active ingredients</li>
                  <li>• Making changes to existing medical treatments</li>
                  <li>• Addressing persistent skin concerns or conditions</li>
                </ul>
              </div>
            </div>
          </AccordionItem>

          {/* Patch Testing Recommended */}
          <AccordionItem
            title="Patch Testing Recommended"
            isOpen={openSections.patchTesting}
            onToggle={() => toggleSection('patchTesting')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600 mb-4">
                Before using any new skincare product, especially those containing active ingredients, 
                we strongly recommend performing a patch test:
              </p>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Patch Testing Steps:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Apply a small amount to a discrete area of skin</li>
                  <li>• Wait 24-48 hours to observe any reactions</li>
                  <li>• Discontinue use if irritation, redness, or other adverse reactions occur</li>
                  <li>• Consult a healthcare professional if reactions persist</li>
                </ul>
              </div>
            </div>
          </AccordionItem>

          {/* Emergency Situations */}
          <AccordionItem
            title="Emergency Situations"
            isOpen={openSections.emergencySituations}
            onToggle={() => toggleSection('emergencySituations')}
          >
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Phone className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Seek Immediate Medical Attention</h4>
                  <p className="text-sm text-gray-600 mb-3">
                    Call emergency services or visit the nearest emergency room if you experience:
                  </p>
                </div>
              </div>
              
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="font-semibold text-red-900 mb-2">Emergency Symptoms:</h4>
                <ul className="text-sm text-red-800 space-y-1">
                  <li>• Severe allergic reactions</li>
                  <li>• Difficulty breathing</li>
                  <li>• Widespread rash or hives</li>
                  <li>• Severe swelling</li>
                  <li>• Signs of infection</li>
                </ul>
              </div>
              
              <CalloutBox type="important">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">When in Doubt</p>
                    <p className="text-sm">
                      If you're unsure whether a reaction requires emergency care, 
                      it's always better to err on the side of caution and seek professional help.
                    </p>
                  </div>
                </div>
              </CalloutBox>
            </div>
          </AccordionItem>
        </div>
      </Container>
      
      <Footer />
    </div>
  );
};

export default MedicalDisclaimerPage;
